import { Chip } from '@mui/material';
import RuleIcon from '@mui/icons-material/Rule';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AdminResourceCrudPage from './components/AdminResourceCrudPage';
import ScienceIcon from '@mui/icons-material/Science';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import { promoteSigmaRule, testSigmaRule, validateSigmaRule } from 'api/securityOps';

const tagLabel = (value) => {
  if (Array.isArray(value)) return value.join(', ');
  return String(value || '');
};

const AdminSigmaRulesPage = () => {
  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'rule_id', label: 'Rule ID', render: (row) => row.rule_id || '-' },
    { key: 'title', label: 'Title', render: (row) => row.title || row.name },
    {
      key: 'level',
      label: 'Level',
      render: (row) => (
        <Chip
          size="small"
          label={String(row.level || 'medium').toUpperCase()}
          sx={{ bgcolor: 'rgba(245,158,11,0.12)', color: '#b45309', fontWeight: 700 }}
        />
      )
    },
    { key: 'tags', label: 'Tags', render: (row) => tagLabel(row.tags) || '-' },
    {
      key: 'is_active',
      label: 'Status',
      render: (row) =>
        row.is_active ? (
          <Chip size="small" label="Active" sx={{ bgcolor: 'rgba(22,163,74,0.12)', color: '#166534', fontWeight: 600 }} />
        ) : (
          <Chip size="small" label="Inactive" sx={{ bgcolor: 'rgba(220,38,38,0.12)', color: '#991b1b', fontWeight: 600 }} />
        )
    },
    {
      key: 'updated_at',
      label: 'Updated',
      render: (row) => (row.updated_at ? new Date(row.updated_at).toLocaleString() : '-')
    }
  ];

  const fields = [
    { name: 'name', label: 'Rule Name', type: 'text', grid: 6 },
    { name: 'is_active', label: 'Active', type: 'switch', grid: 6, defaultValue: true },
    {
      name: 'config',
      label: 'Rule Config (JSON)',
      type: 'json',
      grid: 12,
      defaultJson: {
        kind: 'sigma_rule',
        rule_id: '',
        title: '',
        level: 'medium',
        tags: [],
        logsource: {},
        detection: {},
        content: ''
      }
    }
  ];

  return (
    <AdminResourceCrudPage
      title="Sigma Rules"
      subtitle="Manage Sigma detections used in correlation and threat hunting workflows."
      icon={RuleIcon}
      endpoint="/api/sigma-rules/"
      columns={columns}
      fields={fields}
      rowActions={[
        {
          label: 'Validate',
          icon: VerifiedUserIcon,
          onClick: async (row) => {
            const result = await validateSigmaRule(row.id);
            if (!result?.valid) {
              throw new Error(result?.detail || 'Rule validation failed.');
            }
          }
        },
        {
          label: 'Test',
          icon: ScienceIcon,
          onClick: async (row) => {
            const result = await testSigmaRule(row.id, {
              event: { message: row.title || row.name, severity: row.level || 'medium' }
            });
            if (!result?.matched) {
              throw new Error('Sigma rule test did not match sample event.');
            }
          }
        },
        {
          label: 'Promote',
          icon: UpgradeIcon,
          onClick: async (row) => {
            await promoteSigmaRule(row.id, { stage: 'production' });
          }
        }
      ]}
    />
  );
};

export default AdminSigmaRulesPage;
